import React, { useState, useEffect } from 'react';
import { Button, Box, Stack, CircularProgress, Typography, IconButton } from '@mui/material';
import { useDropzone } from 'react-dropzone';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import ArrowForward from '@mui/icons-material/ArrowForward';
import ArrowBack from '@mui/icons-material/ArrowBack';

import Exercises from '../Exercises';
import SearchExercises from '../SearchExercises';
import HeroBanner from '../HeroBanner';
import Stories from '../../client/pages/Stories';
import { getUserById, addGalleryVideo, getGalleryVideos, checkTrainerApi } from '../../utils/api';
import logo from '../../assets/icons/onryxbrand.png';
// import adImage from '../../assets/icons/מותג.png';
import Intro from '../../assets/icons/Intro.mp4';
// import video2 from '../../assets/icons/2.MP4';
// import video3 from '../../assets/icons/3.mp4';
// import video4 from '../../assets/icons/4.mp4';
// import video5 from '../../assets/icons/5.mp4';
// import video6 from '../../assets/icons/6.mp4';

const Home = ({ userId }) => {
  const [mediaItems, setMediaItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTrainer, setIsTrainer] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [bodyPart, setBodyPart] = useState('all');
  const [showAdPopup, setShowAdPopup] = useState(false);
  const [landingStep, setLandingStep] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (userId) {
          const trainerStatus = await checkTrainerApi(userId);
          setIsTrainer(trainerStatus);
          const user = await getUserById(userId);
          const trainerId = trainerStatus ? user._id : user.trainer_id;
          const response = await getGalleryVideos(trainerId, 'home');
          const parsedItems = response.videos.map((item) => ({
            url: item.fileType.startsWith('video')
              ? `https://pilatesnflow.onrender.com/galleries/stream/${item.fileId}`
              : `data:${item.fileType};base64,${Buffer.from(item.fileBlob.data).toString('base64')}`,
            fileType: item.fileType,
          }));
          setMediaItems(parsedItems);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [userId]);

  useEffect(() => {
    if (mediaItems.length === 0) return;

    const currentItem = mediaItems[currentIndex];
    if (currentItem.fileType.startsWith('image')) {
      const timer = setTimeout(() => {
        setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [currentIndex, mediaItems]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowAdPopup(true);
      localStorage.setItem('adPopupShown', 'true');
    }, 3000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (localStorage.getItem('gymLandingCompleted') !== 'true') {
      setLandingStep(1);
    }
  }, []);

  const handleVideoEnd = () => {
    setCurrentIndex((prev) => (prev + 1) % mediaItems.length);
  };

  const onDrop = async (acceptedFiles) => {
    setIsUploading(true);
    const formData = new FormData();
    formData.append('userId', userId);
    formData.append('videoFile', acceptedFiles[0]);
    formData.append('mediaType', 'home');

    try {
      await addGalleryVideo(formData, (progress) => setUploadProgress(progress));
      setUploadProgress(0);
      const user = await getUserById(userId);
      const response = await getGalleryVideos(user._id, 'home');
      const parsedItems = response.videos.map((item) => ({
        url: item.fileType.startsWith('video')
          ? `https://pilatesnflow.onrender.com/galleries/stream/${item.fileId}`
          : `data:${item.fileType};base64,${Buffer.from(item.fileBlob.data).toString('base64')}`,
        fileType: item.fileType,
      }));
      setMediaItems(parsedItems);
    } catch (error) {
      console.error('Error uploading media:', error);
      setUploadProgress(0);
    } finally {
      setIsUploading(false);
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: 'image/*, video/*',
    disabled: isUploading,
    multiple: false,
  });

  const renderCurrentMedia = () => {
    if (mediaItems.length === 0) {
      if (!isTrainer) {
        return (
          <img
            src={logo}
            alt="Default icon"
            style={{
              width: '100%',
              height: 'auto',
              marginTop: '20px',
              objectFit: 'cover',
              borderRadius: '15px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
            }}
          />
        );
      }
      return null;
    }
    const currentItem = mediaItems[currentIndex];
    return currentItem.fileType.startsWith('video') ? (
      <video
        src={currentItem.url}
        autoPlay
        muted
        onEnded={handleVideoEnd}
        style={{
          width: '100%',
          height: 'auto',
          objectFit: 'cover',
          borderRadius: '15px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        }}
      />
    ) : (
      <img
        src={currentItem.url}
        alt="Home media"
        style={{
          width: '100%',
          height: 'auto',
          objectFit: 'cover',
          borderRadius: '15px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
        }}
      />
    );
  };
  const handleCloseVideoModal = () => {
    localStorage.setItem('gymLandingCompleted','true');
  }

  const renderVideoModal = (videoSrc) => (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
    >
      <video
        src={videoSrc}
        loop
        autoPlay
        muted
        playsInline
        webkit-playsinline
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
        }}
      />
      <IconButton
        onClick={handleCloseVideoModal}
        sx={{
          position: 'absolute',
          top: 10,
          right: 10,
          color: '#FFF',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
          },
        }}
      >
        <CloseIcon />
      </IconButton>
    </Box>
  );
  return (
    <Box>
      <Stories />
      <HeroBanner />
      <SearchExercises setExercises={setExercises} bodyPart={bodyPart} setBodyPart={setBodyPart} />
      <Stack
        sx={{
          position: 'absolute',
          top: { xl: '25%', md: '8%', xs: '20%', lg: '25%', sm: '15%' },
          left: { xs: '8%', xl: '5%', lg: '5%' },
          zIndex: 1,
          width: { xs: '40%', sm: '25%', md: '10%', lg: '20%', xl: '18%' },
          height: 'auto',
        }}
      >
        {isTrainer && mediaItems.length === 0 ? (
          <Box
            {...getRootProps()}
            sx={{
              border: isDragActive ? '2px dashed blue' : '2px dotted gray',
              padding: '20px',
              textAlign: 'center',
              cursor: 'pointer',
              marginBottom: '15px',
              backgroundColor: isUploading ? 'transparent' : '#f0f0f0',
            }}
          >
            <input {...getInputProps()} style={{ display: 'none' }} />
            {isUploading ? (
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress variant="determinate" value={uploadProgress} />
                <Box
                  sx={{
                    top: 5,
                    left: 0,
                    bottom: 0,
                    right: 0,
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {`${uploadProgress}%`}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Stack marginTop="20px" direction="column" alignItems="center">
                <CloudUploadIcon sx={{ fontSize: 50, color: 'gray' }} />
                <Typography variant="h6" sx={{ color: 'gray' }}>
                  העלה גלריה
                </Typography>
                <Typography variant="body2" sx={{ color: 'gray' }}>
                  לחץ או גרור קבצים לכאן
                </Typography>
              </Stack>
            )}
          </Box>
        ) : (
          <Box
            {...(isTrainer ? getRootProps() : {})}
            sx={{
              position: 'relative',
              ...(isTrainer && isDragActive ? { border: '2px dashed blue' } : {}),
            }}
          >
            {isTrainer && <input {...getInputProps()} style={{ display: 'none' }} />}
            {renderCurrentMedia()}
            {isUploading && (
              <Box
                sx={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CircularProgress variant="determinate" value={uploadProgress} />
                <Box
                  sx={{
                    position: 'absolute',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {`${uploadProgress}%`}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        )}
      </Stack>
      <Exercises exercises={exercises} setExercises={setExercises} bodyPart={bodyPart} />
      {landingStep === 1 && renderVideoModal(Intro)}

    </Box>
  );
};

export default Home;